import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Tables } from '@/integrations/supabase/types'
import { useAuth } from './useAuth'

export type Notification = Tables<'notifications'>

export function useNotifications() {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true)
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) {
        console.error('[useNotifications] Erro ao buscar notificações:', error)
        return
      }

      const notificationsList = data || []
      setNotifications(notificationsList)
    } catch (error) {
      console.error('[useNotifications] Erro ao buscar notificações:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_unread_notification_count')

      if (error) {
        console.error('[useNotifications] Erro ao buscar contagem de notificações:', error)
        return
      }

      const count = data || 0
      setUnreadCount(count)
    } catch (error) {
      console.error('[useNotifications] Erro ao buscar contagem de notificações:', error)
    }
  }, [])

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      // Verificar se a notificação estava não lida antes de atualizar
      const notification = notifications.find(n => n.id === notificationId)
      const wasUnread = notification && !notification.is_read

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId)

      if (error) {
        console.error('Erro ao marcar notificação como lida:', error)
        return false
      }

      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true }
            : notif
        )
      )

      // Atualizar contador imediatamente se estava não lida
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1))
      }

      // Refresh unread count para garantir sincronização
      await fetchUnreadCount()
      return true
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error)
      return false
    }
  }, [fetchUnreadCount, notifications])

  const markAllAsRead = useCallback(async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('is_read', false)

      if (error) {
        console.error('Erro ao marcar todas as notificações como lidas:', error)
        return false
      }

      // Update local state
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, is_read: true }))
      )

      // Reset unread count
      setUnreadCount(0)
      return true
    } catch (error) {
      console.error('Erro ao marcar todas as notificações como lidas:', error)
      return false
    }
  }, [])

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      // Verificar se a notificação estava não lida antes de deletar
      const notification = notifications.find(n => n.id === notificationId)
      const wasUnread = notification && !notification.is_read

      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)

      if (error) {
        console.error('Erro ao deletar notificação:', error)
        return false
      }

      // Update local state
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId))
      
      // Atualizar contador imediatamente se estava não lida
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1))
      }

      // Refresh unread count para garantir sincronização
      await fetchUnreadCount()
      return true
    } catch (error) {
      console.error('Erro ao deletar notificação:', error)
      return false
    }
  }, [fetchUnreadCount, notifications])

  // Initial fetch - recarregar quando o usuário mudar (login/logout)
  useEffect(() => {
    if (user) {
      fetchNotifications()
      fetchUnreadCount()
    } else {
      // Limpar quando não há usuário
      setNotifications([])
      setUnreadCount(0)
    }
  }, [user?.id, fetchNotifications, fetchUnreadCount])

  // Listener para evento customizado quando uma notificação é criada
  useEffect(() => {
    const handleNotificationCreated = () => {
      // Forçar atualização do contador e lista
      setTimeout(() => {
        fetchNotifications()
        fetchUnreadCount()
      }, 300)
    }

    window.addEventListener('notification-created', handleNotificationCreated)
    
    return () => {
      window.removeEventListener('notification-created', handleNotificationCreated)
    }
  }, [fetchNotifications, fetchUnreadCount])

  // Set up real-time subscription for new notifications (opcional: VITE_REALTIME_NOTIFICATIONS_ENABLED=false desliga)
  const realtimeEnabled = import.meta.env.VITE_REALTIME_NOTIFICATIONS_ENABLED !== 'false'
  useEffect(() => {
    if (!user || !realtimeEnabled) {
      return
    }

    const channelName = `notifications-${user.id}-${Date.now()}`
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications'
        },
        (payload) => {
          const newNotification = payload.new as Notification
          // A RLS policy garante que só recebemos notificações do usuário atual
          setNotifications(prev => [newNotification, ...prev])
          // Atualizar contador imediatamente
          if (!newNotification.is_read) {
            setUnreadCount(prev => prev + 1)
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications'
        },
        (payload) => {
          const updatedNotification = payload.new as Notification
          const oldNotification = payload.old as Notification
          
          setNotifications(prev => 
            prev.map(notif => 
              notif.id === updatedNotification.id 
                ? updatedNotification 
                : notif
            )
          )
          
          // Atualizar contador imediatamente quando o status de leitura muda
          if (oldNotification?.is_read !== updatedNotification.is_read) {
            if (updatedNotification.is_read) {
              // Notificação foi marcada como lida - diminuir contador
              setUnreadCount(prev => Math.max(0, prev - 1))
            } else {
              // Notificação foi desmarcada como lida - aumentar contador
              setUnreadCount(prev => prev + 1)
            }
            // Também buscar contagem atualizada para garantir sincronização
            fetchUnreadCount()
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications'
        },
        (payload) => {
          const deletedNotification = payload.old as Notification
          
          // Remover da lista local
          setNotifications(prev => prev.filter(notif => notif.id !== deletedNotification.id))
          
          // Atualizar contador imediatamente se a notificação deletada não estava lida
          if (!deletedNotification.is_read) {
            setUnreadCount(prev => Math.max(0, prev - 1))
          }
          // Buscar contagem atualizada para garantir sincronização
          fetchUnreadCount()
        }
      )
      .subscribe((status, err) => {
        // Realtime pode falhar se estiver desabilitado no projeto ou por rede/firewall.
        // A app continua funcionando; notificações serão atualizadas ao recarregar ou ao abrir o painel.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          if (import.meta.env.DEV && err) {
            console.debug('[useNotifications] Realtime indisponível:', err.message)
          }
        }
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user?.id, fetchUnreadCount, realtimeEnabled])

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification
  }
}

